import React, { useEffect, useState } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import { motion } from "framer-motion";
import "./OrgChart.css";

// Define hierarchy levels
const roleHierarchy = [
  "Executive Director",
  "Clinical Director",
  "BCBA",
  "Lead RBT",
  "BT/RBT",
  "Clinical Assistant",
];

// **Animation Variants**
const cardVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.2, duration: 0.4 },
  }),
};

// **Card Component (Styled like Reference Image)**
const OrgCard = ({ staff, index }) => {
  const isMale = staff.gender === "Male";
  const emoji = isMale ? "👨" : "👩"; // Use emojis for male/female
  const borderColor = isMale ? "#0057b7" : "#ff69b4"; // Blue for male, Pink for female

  return (
    <motion.div
      className="org-card"
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}>
      <div className="org-card-content">
        <div className="org-avatar" style={{ borderColor }}>
          {emoji}
        </div>
        <div className="org-text">
          <h4>
            {staff.firstName}
            {staff.lastName}
          </h4>
          <p>{staff.role}</p>
        </div>
      </div>
    </motion.div>
  );
};

// **Org Chart Component**
const OrgChart = ({ data }) => {
  console.log("data", data);
  const [structuredData, setStructuredData] = useState({});

  useEffect(() => {
    // Group staff by role level
    const groupedStaff = {};
    roleHierarchy.forEach((role) => {
      groupedStaff[role] = data?.filter((staff) => staff.role === role);
    });

    setStructuredData(groupedStaff);
  }, [data]);

  const buildTree = (currentLevel) => {
    if (currentLevel >= roleHierarchy.length) return null;

    const currentRole = roleHierarchy[currentLevel];

    // 🔹 Get staff for current level (if any)
    const staffAtCurrentLevel = structuredData[currentRole] || [];

    // 🔹 Find the next available role with data
    let nextAvailableLevel = currentLevel + 1;
    while (
      nextAvailableLevel < roleHierarchy.length &&
      (!structuredData[roleHierarchy[nextAvailableLevel]] ||
        structuredData[roleHierarchy[nextAvailableLevel]].length === 0)
    ) {
      nextAvailableLevel++;
    }

    const nextRole = roleHierarchy[nextAvailableLevel]; // ✅ Get the next filled role

    return (
      <TreeNode
        key={currentRole}
        label={
          <div className="org-tree-level">
            {/* ✅ Horizontal Connector (Only if more than 1 staff at the level) */}
            {staffAtCurrentLevel.length > 1 && <hr />}
            <div className="org-level-container">
              {staffAtCurrentLevel.map((staff, index, arr) => (
                <div
                  key={staff.id}
                  className={`org-card-wrapper ${
                    arr.length === 1
                      ? "single-node"
                      : index === 0
                      ? "leftmost"
                      : index === arr.length - 1
                      ? "rightmost"
                      : ""
                  }`}>
                  <OrgCard staff={staff} index={currentLevel} />
                </div>
              ))}
            </div>
          </div>
        }>
        {/* ✅ Only Show Vertical Line if a Next Role Exists */}
        {nextRole && structuredData[nextRole]?.length > 0 && (
          <TreeNode label={<div className="org-vertical-line"></div>}>
            {buildTree(nextAvailableLevel)}
          </TreeNode>
        )}
      </TreeNode>
    );
  };

  return (
    <div className="org-chart">
      <Tree
        lineWidth={"2px"}
        lineColor={"#aaa"} /* Dashed grey lines */
        lineBorderRadius={"10px"}>
        {buildTree(0)}
      </Tree>
    </div>
  );
};

export default OrgChart;
